import { PropsWithChildren, useContext, useEffect, useState } from "react";
import { useKeycloak } from "@react-keycloak/web";
import { Buffer } from "buffer";
import Spinner from "src/components/Spinner/Spinner";
import { backend } from "src/constants";
import { useLocation } from "react-router-dom";
import type { LocationState } from "src/App";
import { parse } from "query-string";
import { getDiscourseUrl } from "src/services/discuss/discourse";
import { ProfileContext } from "src/providers/profile-provider";
import { getUserAvatar } from "src/utils/user";
import styles from "./DiscourseSSO.module.css";

const DiscourseSSO = ({ children }: PropsWithChildren<{}>) => {
  const { keycloak } = useKeycloak();
  const location = useLocation<LocationState | undefined>();
  const [error, setError] = useState<null | string>(null);
  const { profile } = useContext(ProfileContext);

  useEffect(() => {
    let isCancelled = false;
    const fetchDiscourseSSOAndRedirect = async () => {
      // shouldn't as profile will be defined when keycloak.token is defined
      // but just in case and to make tsc happy
      if (profile === null) {
        setError("Loading profile...");

        return;
      }
      const queryParams = parse(location.search);

      if (!queryParams.sso || !queryParams.sig) {
        setError("Search query parameters sso or sig is required");

        return;
      }
      const avatarUrl = getUserAvatar(profile);
      const discourseSSOServiceUrl = `${backend}/discourse-sso`;

      const requestInit = {
        method: "POST",
        body: JSON.stringify({
          sso: queryParams.sso,
          sig: queryParams.sig,
          avatarUrl,
        }),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${keycloak.token}`,
        },
      };

      if (!isCancelled) {
        const response = await fetch(discourseSSOServiceUrl, requestInit);

        if (response.status < 200 || response.status > 299) {
          setError("Error launching Lens Forums, please try again");
        } else {
          setError(null);
          const json = await response.json();

          // eslint-disable-next-line xss/no-location-href-assign
          window.location.href = getDiscourseUrl(json.sso, json.sig, {
            search: Buffer.from(queryParams.sso as string, "base64").toString("utf8"),
          });
        }
      }
    };

    fetchDiscourseSSOAndRedirect().catch((error: any) => {
      // eslint-disable-next-line no-console
      console.error(error);
    });

    return () => {
      isCancelled = true;
    };
  }, [keycloak.token, location, profile]);

  return (
    <div>
      <Spinner center>
        {error ? <span className={styles.errorMessage}>{error}</span> : <span>Launching Lens Forums ...</span>}
        {children}
      </Spinner>
    </div>
  );
};

export default DiscourseSSO;
